// Camera + mic → WebCodecs → one MPEG-TS blob per encoded frame (flow 1).
//
// Both tracks are read through MediaStreamTrackProcessor so VideoFrame and
// AudioData timestamps come from one capture clock; the muxer relies on that
// for A/V sync (see ts/muxer.ts). Video always leaves at 1280x720 and at most
// 30 fps whatever the camera delivers (raster.ts: JBS drops any other raster). The encoders outlive any one RIFT session:
// a reconnect swaps the sink and asks for a keyframe, it does not rebuild the
// pipeline.
import { toAdts } from './aac/adts.js';
import { AUDIO_CODEC, AUDIO_SAMPLE_RATE, VIDEO_CODEC } from './capabilities.js';
import { FrameDecimator, letterbox, UPLINK_FPS, UPLINK_HEIGHT, UPLINK_WIDTH } from './raster.js';
import { TsMuxer } from './ts/muxer.js';
export const DEFAULT_UPLINK = {
    videoBitrateBps: 2_000_000,
    minVideoBitrateBps: 300_000,
    keyframeIntervalMs: 2_000,
    audioBitrateBps: 96_000,
};
export class Uplink {
    stream;
    audioEnabled;
    opts;
    muxer;
    videoEncoder = null;
    audioEncoder = null;
    sink = null;
    forceKey = true;
    lastKeyUs = -Infinity;
    currentBitrate;
    running = false;
    readers = [];
    audioChannels = 2;
    audioSampleRate = AUDIO_SAMPLE_RATE;
    decimator = new FrameDecimator(UPLINK_FPS);
    letterboxCanvas = null;
    stats = {
        capturedFrames: 0,
        videoFrames: 0,
        audioFrames: 0,
        bytes: 0,
        droppedFrames: 0,
        decimatedFrames: 0,
        letterboxedFrames: 0,
    };
    encoderFps = UPLINK_FPS;
    constructor(stream, audioEnabled, opts = {}) {
        this.stream = stream;
        this.audioEnabled = audioEnabled;
        this.opts = { ...DEFAULT_UPLINK, ...opts };
        this.currentBitrate = this.opts.videoBitrateBps;
        this.muxer = new TsMuxer({ audio: audioEnabled });
    }
    get videoBitrateBps() {
        return this.currentBitrate;
    }
    /** The rate the camera track negotiated; it can still deliver fewer (e.g. in low light). */
    get cameraFps() {
        return this.stream.getVideoTracks()[0]?.getSettings().frameRate ?? null;
    }
    /** Route output to a (new) session. The next video frame is a keyframe. */
    setSink(sink) {
        this.sink = sink;
        this.forceKey = true;
    }
    requestKeyframe() {
        this.forceKey = true;
    }
    /** Clamp to [min, configured max] and reconfigure the encoder in place. */
    setVideoBitrate(bps) {
        const target = Math.round(Math.max(this.opts.minVideoBitrateBps, Math.min(this.opts.videoBitrateBps, bps)));
        if (Math.abs(target - this.currentBitrate) < this.currentBitrate * 0.05)
            return;
        this.currentBitrate = target;
        if (this.videoEncoder && this.videoEncoder.state === 'configured') {
            this.videoEncoder.configure(this.videoConfig());
        }
    }
    videoConfig() {
        return {
            codec: VIDEO_CODEC,
            width: UPLINK_WIDTH,
            height: UPLINK_HEIGHT,
            bitrate: this.currentBitrate,
            framerate: UPLINK_FPS,
            latencyMode: 'realtime',
            avc: { format: 'annexb' },
        };
    }
    async start() {
        const Processor = globalThis
            .MediaStreamTrackProcessor;
        this.running = true;
        const videoTrack = this.stream.getVideoTracks()[0];
        if (!videoTrack)
            throw new Error('No camera track');
        this.videoEncoder = new VideoEncoder({
            output: (chunk) => this.onVideoChunk(chunk),
            error: (e) => console.error('[rift-guest] video encoder error', e),
        });
        this.videoEncoder.configure(this.videoConfig());
        void this.pump(new Processor({ track: videoTrack }).readable, (f) => this.encodeVideo(f));
        const audioTrack = this.audioEnabled ? this.stream.getAudioTracks()[0] : undefined;
        if (audioTrack) {
            // Configured on the first AudioData: the encoder must match the
            // capture's rate and channel count, which the track settings do not
            // reliably report before audio flows.
            this.audioEncoder = new AudioEncoder({
                output: (chunk) => this.onAudioChunk(chunk),
                error: (e) => console.error('[rift-guest] audio encoder error', e),
            });
            void this.pump(new Processor({ track: audioTrack }).readable, (a) => this.encodeAudio(a));
        }
    }
    async pump(readable, handle) {
        const reader = readable.getReader();
        this.readers.push(reader);
        while (this.running) {
            const { value, done } = await reader.read().catch(() => ({ value: undefined, done: true }));
            if (done || !value)
                break;
            try {
                handle(value);
            }
            finally {
                value.close();
            }
        }
    }
    encodeVideo(frame) {
        this.stats.capturedFrames++;
        const enc = this.videoEncoder;
        if (!enc || enc.state !== 'configured')
            return;
        // Realtime: never queue behind a slow encoder, drop and catch up.
        if (enc.encodeQueueSize > 2) {
            this.stats.droppedFrames++;
            return;
        }
        if (!this.decimator.admit(frame.timestamp)) {
            this.stats.decimatedFrames++;
            return;
        }
        const due = frame.timestamp - this.lastKeyUs >= this.opts.keyframeIntervalMs * 1000;
        const keyFrame = this.forceKey || due;
        if (keyFrame) {
            this.forceKey = false;
            this.lastKeyUs = frame.timestamp;
        }
        const fitted = this.fitRaster(frame);
        try {
            enc.encode(fitted, { keyFrame });
        }
        finally {
            if (fitted !== frame)
                fitted.close();
        }
    }
    /** The frame itself when it is already 1280x720, else a letterboxed copy. */
    fitRaster(frame) {
        const w = frame.displayWidth;
        const h = frame.displayHeight;
        if (w === UPLINK_WIDTH && h === UPLINK_HEIGHT)
            return frame;
        this.letterboxCanvas ??= new OffscreenCanvas(UPLINK_WIDTH, UPLINK_HEIGHT);
        const ctx = this.letterboxCanvas.getContext('2d');
        if (!ctx)
            return frame;
        const r = letterbox(w, h);
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, UPLINK_WIDTH, UPLINK_HEIGHT);
        ctx.drawImage(frame, r.x, r.y, r.width, r.height);
        this.stats.letterboxedFrames++;
        return new VideoFrame(this.letterboxCanvas, { timestamp: frame.timestamp, duration: frame.duration ?? undefined });
    }
    encodeAudio(data) {
        const enc = this.audioEncoder;
        if (!enc || enc.state === 'closed')
            return;
        if (enc.state === 'unconfigured') {
            this.audioSampleRate = data.sampleRate;
            this.audioChannels = Math.min(2, data.numberOfChannels);
            const cfg = {
                codec: AUDIO_CODEC,
                sampleRate: this.audioSampleRate,
                numberOfChannels: this.audioChannels,
                bitrate: this.opts.audioBitrateBps,
                aac: { format: 'aac' },
            };
            enc.configure(cfg);
        }
        enc.encode(data);
    }
    onVideoChunk(chunk) {
        const annexb = new Uint8Array(chunk.byteLength);
        chunk.copyTo(annexb);
        const keyframe = chunk.type === 'key';
        const bytes = this.muxer.muxVideo(annexb, chunk.timestamp, keyframe);
        this.stats.videoFrames++;
        this.emit({ bytes, keyframe, kind: 'video' });
    }
    onAudioChunk(chunk) {
        const raw = new Uint8Array(chunk.byteLength);
        chunk.copyTo(raw);
        const adts = toAdts({ sampleRate: this.audioSampleRate, channels: this.audioChannels }, raw);
        const bytes = this.muxer.muxAudio(adts, chunk.timestamp);
        this.stats.audioFrames++;
        this.emit({ bytes, keyframe: false, kind: 'audio' });
    }
    emit(p) {
        if (!this.sink)
            return;
        this.stats.bytes += p.bytes.length;
        this.sink(p);
    }
    async stop() {
        this.running = false;
        for (const r of this.readers)
            await r.cancel().catch(() => undefined);
        this.readers.length = 0;
        for (const enc of [this.videoEncoder, this.audioEncoder]) {
            if (enc && enc.state !== 'closed')
                enc.close();
        }
        this.videoEncoder = null;
        this.audioEncoder = null;
        this.sink = null;
    }
}
//# sourceMappingURL=uplink.js.map